import "./header.css";

import { HeaderProfileIcon, SmartSenseLogo } from "./../svg";
import { inject, observer } from "mobx-react";
import { AuthStore, TransitionStore } from "../../store";
import webResource from "../../modules/login/web";
import { useNavigate } from "react-router-dom";
import { CreateConfirmationDialog } from "../../utils/ConfirmationDialog";
import OverflowLabel from "./../OverflowLabel";
import TreeSelect from "./../tree-select/TreeSelect";
import { useEffect, useRef, useState } from "react";
import { readLocationList, updateLocationSelectedIds } from "./web";
import LocationSelectorStore from "../../store/location-selector-store";
import { Button } from "@mui/material";
import { LocationType } from "./types/LocationTypes";

const RETRY_FETCH_MS = 10000;

export interface HeaderProps {
    authStore?: AuthStore;
    transitionStore?: TransitionStore;
    locationSelectorStore?: LocationSelectorStore;
    t(value: string): string;
}

function Header(props: HeaderProps) {
    const { t, authStore, transitionStore } = props;
    const locationSelectorStore = props.locationSelectorStore!;
    const firstname = authStore?.Firstname();
    const lastname = authStore?.Lastname();
    const history = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const defaultExpandedList = useRef([""]);

    const { indexedLocationList, locationList, selectedIds } =
        locationSelectorStore;

    useEffect(() => {
        readLocations(true);
    }, []);

    const readLocations = (showSpinner: boolean) => {
        if (showSpinner) setIsLoading(true);
        readLocationList()
            .then((response) => {
                let locations: any[] = response.data.locations;
                const selectedIds = response.data.selectedLocationIds;

                if (locations.length === 0) {
                    setTimeout(() => readLocations(false), RETRY_FETCH_MS);
                    return;
                }

                const locationList = locations.map((location: any) => {
                    return mapLocationItem(location);
                });

                locationSelectorStore?.setLocationList(locationList);
                locationSelectorStore!.setSelectedIds(
                    selectedIds.map((id: number) => {
                        return id + "";
                    })
                );
                indexItemList(locationList);
                locationSelectorStore.setRequestIds(selectedIds);

                locationSelectorStore.setInitialized(true);
            })
            .finally(() => {
                if (showSpinner) setIsLoading(false);
            });
    };

    const indexItemList = (itemList: any[]) => {
        const indexedList: any = {};
        const expandedList: string[] = [];
        const selectedRoomList: any[] = [];

        const indexNode = (node: any, parentId: string | null) => {
            let isExpanded = false;
            indexedList[node.id + ""] = { node: node, parentId: parentId };
            if (
                node.type === LocationType.ROOM &&
                (locationSelectorStore.selectedIds.length === 0 ||
                    locationSelectorStore.selectedIds.includes(node.id + ""))
            ) {
                selectedRoomList.push({
                    id: node.id,
                    name: node.name,
                    children: [],
                    path: node.path,
                    hasTelecareCapability: node.hasTelecareCapability,
                });
            }

            if (node.children) {
                node.children.map((child: any) => {
                    if (child.type !== LocationType.ROOM && !isExpanded) {
                        expandedList.push(node.id);
                        isExpanded = true;
                    }

                    indexNode(child, node.id);
                });
            }
        };

        itemList.map((item: any) => {
            indexNode(item, null);
        });

        defaultExpandedList.current = expandedList;
        locationSelectorStore?.setIndexedLocationList(indexedList);
        locationSelectorStore?.setSelectedRoomList(selectedRoomList);
    };

    const getSelectedItemIds = () => {
        let idsList: string = "";

        const getNameOfChild = (node: any) => {
            if (selectedIds!.includes(node.id)) {
                if (idsList === "") {
                    idsList += node.id + "";
                } else {
                    idsList += " | " + node.id;
                }
            } else {
                if (node.children) {
                    node.children.map((child: any) => {
                        getNameOfChild(child);
                    });
                }
            }
        };

        locationList.map((item) => {
            getNameOfChild(item);
        });

        if (idsList === "") {
            idsList = "All";
        }

        return idsList;
    };

    const mapLocationItem = (location: any) => {
        const item = {
            id: "",
            name: "",
            type: 0,
            children: [],
            path: "",
            hasTelecareCapability: false,
        };
        item.id = location.id + "";
        item.name = location.name;
        item.type = location.locationType;
        item.path = location.path;
        item.hasTelecareCapability = location.hasTelecareCapability;

        if (location.childLocations) {
            item.children = location.childLocations.map(
                (childLocation: any) => {
                    return mapLocationItem(childLocation);
                }
            );
        }

        return item;
    };

    const displayedText = () => {
        let text = "";

        const list = locationSelectorStore.locationList;
        const selectedIds = locationSelectorStore.requestIds;

        if (selectedIds.length === 0) return "All";

        const mapChildern = (parent: any) => {
            if (selectedIds.includes(Number(parent.id))) {
                if (text === "") text += parent.name + " ";
                else text += ", " + parent.name;
                return;
            } else {
                if (parent.children && parent.children.length > 0) {
                    parent.children.map((child: any) => mapChildern(child));
                }
            }
        };

        list.map((element) => {
            if (element.children && element.children.length > 0) {
                mapChildern(element);
            }
        });

        return text;
    };

    const onToggleChange = (id: string, isSelected: boolean) => {
        const selectedNode = indexedLocationList[id].node;

        const list: any[] = [];
        const list2: any[] = [];

        const toggleChildren = (node: any) => {
            if (!selectedIds!.includes(node.id)) {
                list.push(node.id);
            }
            if (node.children) {
                node.children.map((node: any) => {
                    if (node) toggleChildren(node);
                });
            }
            return list;
        };

        const untoggleChildren = (node: any) => {
            list.push(node.id);

            if (node.children) {
                node.children.map((node: any) => {
                    untoggleChildren(node);
                });
            }
            return list;
        };

        const untoggleParents = (parentId: string | null) => {
            if (parentId !== null) {
                list2.push(parentId);
                if (indexedLocationList[parentId].parentId !== null) {
                    untoggleParents(indexedLocationList[parentId].parentId);
                }
            }

            return list;
        };

        if (isSelected) {
            toggleChildren(selectedNode);
            locationSelectorStore?.setSelectedIds(selectedIds.concat(list));
        } else if (!isSelected) {
            untoggleChildren(selectedNode);
            untoggleParents(indexedLocationList[id].parentId);

            locationSelectorStore?.setSelectedIds(
                selectedIds!.filter(
                    (x: string) => !list.includes(x) && !list2.includes(x)
                )
            );
        }
    };

    const render = () => {
        return (
            <div className="layout-header">
                <div className="left-content">
                    <div className="title-container">
                        <SmartSenseLogo onClick={handleLogoClick} />
                        <div className="title">{t("HeaderTitle")}</div>
                    </div>
                    <div className="tree-container">
                        <div className="header-tree-select">
                            {(!isLoading && (
                                <>
                                    {(locationList.length === 0 && (
                                        <TreeSelect
                                            items={locationList}
                                            onToggleChange={onToggleChange}
                                            displayedText={displayedText()}
                                            disabled
                                        />
                                    )) || (
                                        <TreeSelect
                                            items={locationList}
                                            selectedItems={selectedIds}
                                            onToggleChange={onToggleChange}
                                            displayedText={displayedText()}
                                            onSelectClick={() => {
                                                updateLocationSelectedIds(
                                                    getSelectedItemIds()
                                                ).then(() => {
                                                    readLocations(true);
                                                });
                                            }}
                                            defaultExpandedIds={
                                                defaultExpandedList.current
                                            }
                                        />
                                    )}
                                </>
                            )) || (
                                <Button variant="outlined" disabled>
                                    <div
                                        className={
                                            "location-selector-container"
                                        }
                                    >
                                        <div
                                            className={
                                                "location-selector-loader"
                                            }
                                        />
                                    </div>
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="profile-container">
                    <div className="profile-icon">
                        <HeaderProfileIcon />
                    </div>
                    <div className="profile-name">
                        <OverflowLabel text={firstname + " " + lastname} />
                    </div>
                    <div className="separator" />
                    <div onClick={handleLogoutClick} className="logout-button">
                        {t("HeaderLogout")}
                    </div>
                </div>
            </div>
        );
    };

    const handleLogoClick = () => {
        history("/residents-overview");
    };

    const handleLogoutClick = () => {
        const confirmationDialogData = CreateConfirmationDialog(
            t("HeaderLogout"),
            t("LogOutContent"),
            t("Yes"),
            t("No"),
            false,
            handleLogout
        );

        transitionStore!.showGlobalDialog(confirmationDialogData);
    };

    const handleLogout = () => {
        webResource
            .requestLogout()
            .then(() => {
                window.location.href = "/";
            })
            .finally(() => {
                authStore?.clearAuthentication();
            });
    };

    return render();
}

const injectedComponent = inject(
    "authStore",
    "transitionStore",
    "locationSelectorStore"
)(observer(Header));

export { injectedComponent as Header };
